import React, { MutableRefObject, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';

import { AppId, GroupLayout, StandardAspectRatio } from '@skytvnz/sky-app-store/lib/types/graph-ql';
import { actions } from '@/Store';
import usePagination from '@/Hooks/usePagination';
import Rail from '@/Components/Rail';
import TileType from '@/Components/Rail/Tiles/TileType';
import TileSize from '@/Components/Rail/Tiles/TileSize';
import RailGroup from '@/types/enums/RailGroup';
import { useQuery } from '@apollo/client';
import { ROOT_SECTIONS_QUERY, RootSectionsData } from '@/Hooks/useRootSections';
import { Section } from '@/lib/graph-ql';

interface Props {
  // @todo: change rails type from any
  rails: any;
  LastPaginatedRail: MutableRefObject<{ id: string; index: number }>;
  isNewHistory: boolean;
  indexOnPage: number;
}
const RailWrapper: React.FC<Props> = ({
  rails,
  LastPaginatedRail,
  isNewHistory,
  indexOnPage = 1,
}) => {
  const dispatch = useDispatch();
  const { railPageSize } = usePagination();
  const lastPaginatedRailRef = LastPaginatedRail;
  const location = useLocation();
  const { data: rootSectionsData } = useQuery<RootSectionsData<Section[]>>(ROOT_SECTIONS_QUERY);

  const onTileClick = useCallback(
    (railId: string, positionIndex: number) => {
      const isContinueWatchingRail = rails?.some(
        rail => rail.id === railId && rail.layout === GroupLayout.ContinueWatching,
      );
      if (isContinueWatchingRail) {
        lastPaginatedRailRef.current = { id: '', index: 0 };
      } else {
        lastPaginatedRailRef.current = { id: railId, index: positionIndex };
      }
    },
    [rails, lastPaginatedRailRef],
  );
  const onTilesPaginate = useCallback(
    id => {
      setTimeout(() => {
        const path = location.pathname;
        const currentPage = rootSectionsData?.rootSections.find(
          ({ home }) => path === home?.path,
        ) as Section;

        dispatch(
          actions.home.fetchRailById(id, currentPage.id, AppId.SkyGoWeb, railPageSize.current),
        );
        // the timeout value equals to the paginate transition duration
      }, 600);
    },
    [dispatch, location.pathname, railPageSize, rootSectionsData],
  );

  return (
    <>
      {rails &&
        rails
          .filter(rail => rail.contentPage)
          .map(({ contentPage: content, ...rail }, index) => {
            let contentPage = content;

            if (rail.__typename === RailGroup.FeatureRail) {
              contentPage = {
                pageInfo: content.pageInfo,
                content: content.content,
              };
            }

            return (
              <Rail
                indexOnPage={indexOnPage + index}
                key={`rail_${rail.id}`}
                id={rail.id}
                heading={rail.title || ''}
                subTitle={rail.subtitle || ''}
                type={TileType.Media}
                assets={contentPage.content}
                layout={rail.layout || GroupLayout.RailLandscape}
                railGroup={rail.__typename}
                size={
                  rail?.aspectRatio === StandardAspectRatio._1_1
                    ? TileSize.SmallSquare
                    : TileSize.Small
                }
                hasNextPage={contentPage.pageInfo?.hasNextPage}
                hasError={!!rail.error}
                isTilesLoading={rail.isLoading}
                initialIndex={
                  !isNewHistory && lastPaginatedRailRef.id === rail.id
                    ? lastPaginatedRailRef.index
                    : 0
                }
                tileClickHandler={onTileClick}
                tilesLoadHandler={onTilesPaginate}
              />
            );
          })}
    </>
  );
};
export default RailWrapper;
